/*
THE single place Global Search destinations become navigation (per the plan:
the server sends canonical URLs for copy-link/new-tab, but the SPA navigates
by kind + nativeIds through vue-router — URL-format changes never touch the
index, and in-app navigation never parses URLs).
*/
import { RawLocation } from 'vue-router';
import {
	SEARCH_ARTICLE_URL_ALLOWED_HOSTS,
	SearchDestinationBoard,
	SearchDestinationExternalArticle,
	SearchDestinationLibraryResult,
	SearchDestinationTemplate,
	SearchDestinationTool,
} from '@/types/search';
import { ROUTER } from '@/router/constants';

export type ResolvedSearchDestination =
	| { location: RawLocation, externalUrl?: undefined }
	| { location?: undefined, externalUrl: string };

// Navigation reads only `kind` and `nativeIds` (an article its `url`), so a
// destination built client-side, like the modal's Jump back in rows, needs no
// label or permalink. Every server-sent SearchDestination fits.
export type RoutableSearchDestination =
	| Pick<SearchDestinationLibraryResult, 'kind' | 'nativeIds'>
	| Pick<SearchDestinationBoard, 'kind' | 'nativeIds'>
	| Pick<SearchDestinationTemplate, 'kind' | 'nativeIds'>
	| Pick<SearchDestinationTool, 'kind' | 'nativeIds'>
	| Pick<SearchDestinationExternalArticle, 'kind' | 'url'>;

export const resolveSearchDestination = (
	destination: RoutableSearchDestination,
): ResolvedSearchDestination | null => {
	switch (destination.kind) {
		case 'LIBRARY_RESULT':
			return {
				location: {
					name: ROUTER.RUNNITS_LIBRARY,
					query: {
						nodeRunId: destination.nativeIds.nodeRunId,
						resultUuid: destination.nativeIds.resultUuid,
					},
				},
			};
		case 'BOARD':
			return {
				location: {
					name: ROUTER.RUNNITS_BOARD,
					params: { runnitId: destination.nativeIds.runnitId },
				},
			};
		case 'TEMPLATE':
			// The board route renders the template view for TEMPLATE runnits.
			return {
				location: {
					name: ROUTER.RUNNITS_BOARD,
					params: { runnitId: destination.nativeIds.templateId },
				},
			};
		case 'TOOL':
			return {
				location: {
					name: ROUTER.RUNNIT_REROUTE,
					query: { toolId: destination.nativeIds.nodeDefId },
				},
			};
		case 'EXTERNAL_ARTICLE': {
			// Allowlisted hosts only — never open arbitrary server-sent URLs.
			try {
				const url = new URL(destination.url);
				if (url.protocol === 'https:' && SEARCH_ARTICLE_URL_ALLOWED_HOSTS.includes(url.host)) {
					return { externalUrl: destination.url };
				}
			} catch (e) { /* malformed URL → null */ }
			return null;
		}
		default:
			return null;
	}
};

// --- The inbound half of a LIBRARY_RESULT destination -----------------------
// `resolveSearchDestination` WRITES { nodeRunId, resultUuid } into the library
// route's query; these read it back. They live beside their writer so the two
// halves of the round trip stay in step.

export interface PermalinkTarget {
	nodeRunId: string;
	resultUuid: string;
}

/**
 * The permalinked result as ONE value, so a Vue watcher fires on a change to
 * either half.
 *
 * Both halves matter: a run holds several results, so two search hits can
 * differ only in `resultUuid`. Watching the run id alone leaves the carousel
 * showing the previous result while the URL names the new one.
 *
 * Empty string when the query names no permalink, which is what closing the
 * carousel produces (the params are stripped).
 */
export const permalinkTargetKey = (query: Record<string, any> | null | undefined): string => {
	const nodeRunId: string = String((query && query.nodeRunId) || '');
	if (!nodeRunId) return '';
	return `${nodeRunId}|${String((query && query.resultUuid) || '')}`;
};

/** Split a key from `permalinkTargetKey` back into its parts, or null. */
export const parsePermalinkTargetKey = (key: string): PermalinkTarget | null => {
	if (!key) return null;
	const separator: number = key.indexOf('|');
	const nodeRunId: string = separator === -1 ? key : key.slice(0, separator);
	if (!nodeRunId) return null;
	return {
		nodeRunId,
		resultUuid: separator === -1 ? '' : key.slice(separator + 1),
	};
};
